import { plainToClass, Type } from "class-transformer";
import { cloneDeep } from "lodash";
import { BusinessAuto, InlandMarineEquipment } from ".";
import { NumberFormatter } from "../util";
import { Attribute } from "./Attribute";
import { AttributeOption } from "./AttributeOption";
import { Comparison } from "./Comparison/Comparison";
import { CoverageCompositeRating } from "./CoverageCompositeRating";
import { CoverageLimit } from "./CoverageLimit";
import { CoverageLimitOption } from "./CoverageLimitOption";
import { CoverageLinkedBusinessAuto } from "./CoverageLinkedBusinessAuto";
import { CoverageLinkedDriver } from "./CoverageLinkedDriver";
import { CoverageLinkedTool } from "./CoverageLinkedTool";
import { CoverageLinkedTractor } from "./CoverageLinkedTractor";
import { CoverageLinkedTrailer } from "./CoverageLinkedTrailer";
import { CoverageModification } from "./CoverageModification";
import { CoverageOption } from "./CoverageOption";
import { CoverageRateClassification } from "./CoverageRateClassification";
import { CoverageRateOwner } from "./CoverageRateOwner";
import { CoverageScheduleOfHazard } from "./CoverageScheduleOfHazard";
import { CoverageUnderlyingCoverage } from "./CoverageUnderlyingCoverage";
import { Driver } from "./Driver";
import { Policy } from "./Policy";
import { Tractor } from "./Tractor";
import { Trailer } from "./Trailer";

namespace CoverageEnums {
    export enum Basis {
        FEE = "Fee",
        FLAT = "Flat",
        MILEAGE = "Mileage",
        NONE = "None",
        INSTALLMENT = "Installment",
        PAYROLL = "Payroll",
        PREMIUM = "Premium",
        REVENUE = "Revenue",
        UNITS = "Units",
        VALUES = "Values",
    }

    export enum Frequency {
        MONTHLY = "Monthly",
        QUARTERLY = "Quarterly",
        YEARLY = "Yearly",
    }
}

class _Summary<T extends Coverage.ICanGenerateAbbreviation> {
    #coverage: T;
    #policy: Partial<Pick<Policy, "description">>;
    readonly id: CoverageOption.Id;
    readonly abbreviation: CoverageOption.Abbreviation;
    readonly name: CoverageOption.Name;
    readonly sortOrder: number;
    constructor(coverage: T, policy?: Partial<Pick<Policy, "description">>) {
        this.#coverage = coverage;
        this.#policy = policy;

        if (this.#coverage.limits) {
            this.#coverage.limits = this.#coverage.limits.filter((l) => typeof l.coverageLimitOptionId === "number");
        }

        const coverageOptionMetadata = CoverageOption.getMetadata(coverage.coverageOptionId);

        this.id = coverageOptionMetadata.id;
        this.abbreviation = Coverage.getAbbreviation({ coverage: this.#coverage, policyDescription: this.#policy?.description });
        this.name = coverageOptionMetadata.name;
        this.sortOrder = coverageOptionMetadata.defaultSortOrder;
    }
}

class _Summaries<T extends Coverage.ICanGenerateAbbreviation> {
    #coverages: T[];
    #policy: Partial<Pick<Policy, "description">>;
    readonly summaries: _Summary<T>[];
    #valid = false;
    constructor(coverages?: T[], policy?: Partial<Pick<Policy, "description">>) {
        this.#coverages = (coverages || []).filter((c) => typeof c.coverageOptionId === "number");
        this.#policy = policy;
        this.#valid = this.#coverages.length > 0;

        this.summaries = this.#coverages.filter((c) => typeof c.coverageOptionId === "number").map((c) => new _Summary<T>(c, policy));
    }

    sort(): this {
        this.summaries.sort((a, b) => {
            if (a.sortOrder < b.sortOrder) return -1;
            if (a.sortOrder > b.sortOrder) return 1;
            return 0;
        });
        return this;
    }

    getDescription(): string {
        if (!this.#valid) return this.#policy.description || "";

        return this.getAbbreviations().join(", ");
    }

    getAbbreviations(): string[] {
        if (!this.#valid) return [];
        this.sort();
        return Array.from(new Set(this.summaries.map((m) => m.abbreviation)));
    }
}

export class Coverage implements Coverage.IHasCoverageOptionId, Comparison.ICanCompare<Coverage> {
    static Basis = {
        ...CoverageEnums.Basis,
        getAvailableOptions(): CoverageEnums.Basis[] {
            return [
                CoverageEnums.Basis.FEE,
                CoverageEnums.Basis.MILEAGE,
                CoverageEnums.Basis.NONE,
                CoverageEnums.Basis.INSTALLMENT,
                CoverageEnums.Basis.PAYROLL,
                CoverageEnums.Basis.PREMIUM,
                CoverageEnums.Basis.REVENUE,
                CoverageEnums.Basis.UNITS,
                CoverageEnums.Basis.VALUES,
            ];
        },
        getValues(): CoverageEnums.Basis[] {
            return Object.values(CoverageEnums.Basis);
        },
    };
    static Frequency = CoverageEnums.Frequency;
    static Summary = _Summary;
    static Summaries = _Summaries;

    #initialized = false;
    static init(coverage: Coverage): Coverage {
        if (coverage.#initialized) return coverage;

        const e = plainToClass(Coverage, coverage);
        e.linkedTractors = e.linkedTractors.map((linkedTractor) => CoverageLinkedTractor.init({ coverage, linkedTractor }));
        e.linkedTrailers = e.linkedTrailers.map((linkedTrailer) => CoverageLinkedTrailer.init({ coverage, linkedTrailer }));
        e.linkedBusinessAutos = e.linkedBusinessAutos.map((linkedBusAuto) => CoverageLinkedBusinessAuto.init({ coverage, linkedBusAuto }));
        e.linkedTools = e.linkedTools.map((linkedTool) => CoverageLinkedTool.init({ coverage, linkedTool }));
        e.linkedDrivers = e.linkedDrivers.map((linkedDriver) => CoverageLinkedDriver.init({ coverage, linkedDriver }));
        e.limits = e.limits.filter((l) => typeof l.coverageLimitOptionId === "number").map((limit) => CoverageLimit.new(limit));

        e.#initialized = true;

        return e;
    }

    static getMetadataFromAbbreviation(abbreviation: CoverageOption.Abbreviation): CoverageOption.IMetadataWithAbbreviation {
        return CoverageOption.getMetadataFromAbbreviation(abbreviation);
    }

    static isComposite(policy: Policy, coverage: Coverage): boolean {
        return Policy.isComposite(policy) && (coverage.basis !== Coverage.Basis.NONE || coverage.compositeRatings?.length > 0);
    }

    static getAbbreviation({
        coverage,
        policyDescription,
        options,
    }: {
        coverage: Coverage.ICanGenerateAbbreviation;
        policyDescription?: Policy["description"];
        options?: {
            includeBondNameOnly: true;
        };
    }): CoverageOption.Abbreviation | null {
        if (!coverage) return null;

        const coverageOptionMetadata = CoverageOption.getMetadata(coverage.coverageOptionId);
        if (!coverageOptionMetadata) {
            if (policyDescription) return Coverage.#getAbbreviationFromDescription(coverage, policyDescription);
            return null;
        }

        const hasRelated = {
            businessAutos: (coverage.linkedBusinessAutos?.length || 0) > 0,
            tractors: (coverage.linkedTractors?.length || 0) > 0,
            trailers: (coverage.linkedTrailers?.length || 0) > 0,
        };
        const hasLinkedItems = Boolean(coverage.linkedBusinessAutos && coverage.linkedTractors && coverage.linkedTrailers);
        if (coverage.underlyingCoverages && coverage.coverageOptionId === CoverageOption.Id.EXCESS_LIABILITY) {
            const underlyingCoverages = coverage.underlyingCoverages.map((uc) => uc.description) || [];
            if (underlyingCoverages.includes(CoverageUnderlyingCoverage.Description.AUTO_LIABILITY)) {
                return "XSAL";
            } else if (underlyingCoverages.includes(CoverageUnderlyingCoverage.Description.GENERAL_LIABILITY)) {
                return "XSGL";
            }

            return coverageOptionMetadata.defaultAbbreviation;
        } else if (
            coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE &&
            coverage.limits?.length === 1 &&
            coverage.limits[0].coverageLimitOptionId === CoverageLimitOption.Id.TRAILER_INTERCHANGE
        ) {
            return "TI";
        } else if (hasLinkedItems && coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE) {
            if (!hasRelated.businessAutos && !hasRelated.tractors && !hasRelated.trailers) {
                // When Physical Damage has no equipment related, change product abbreviation to "HAPD"
                return "HAPD";
            } else if (hasRelated.businessAutos && !hasRelated.tractors && !hasRelated.trailers) {
                // When Physical Damage has only Business Autos related, change product abbreviation to "BAPD"
                return "BAPD";
            }
            return coverageOptionMetadata.defaultAbbreviation;
        } else if (
            hasLinkedItems &&
            coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_AUTO_LIABILITY &&
            hasRelated.businessAutos &&
            !hasRelated.tractors &&
            !hasRelated.trailers
        ) {
            // When Auto Liability has only Business Autos related, change product abbreviation to "BAL"
            return "BAL";
        } else if (coverage.coverageOptionId === CoverageOption.Id.BONDS) {
            const bondNameAttribute = coverage?.attributes?.find(
                (bondNameAttribute) => bondNameAttribute.attributeOptionId === AttributeOption.Id.BOND_NAME
            );
            if (!bondNameAttribute) {
                return "BOND";
            }
            if (options?.includeBondNameOnly) {
                return bondNameAttribute.valueText as CoverageOption.Abbreviation;
            }
            return `BOND (${bondNameAttribute.valueText})`;
        } else {
            if (policyDescription) return Coverage.#getAbbreviationFromDescription(coverage, policyDescription);

            return coverageOptionMetadata.defaultAbbreviation;
        }
    }

    static #getAbbreviationFromDescription(
        coverage: Coverage.IHasCoverageOptionId,
        description: string
    ): CoverageOption.Abbreviation | null {
        if (!coverage || !description) return null;
        const coverageOptionMetadata = CoverageOption.getMetadata(coverage.coverageOptionId);
        if (!coverageOptionMetadata) return null;

        const parts = new Set(description.split(",").map((p) => p.trim()) as CoverageOption.Abbreviation[]);
        if (coverage.coverageOptionId === CoverageOption.Id.EXCESS_LIABILITY) {
            if (parts.has("XSAL")) {
                return "XSAL";
            } else if (parts.has("XSGL")) {
                return "XSGL";
            } else {
                return "XS";
            }
        } else if (coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE) {
            if (parts.has("HAPD")) {
                return "HAPD";
            } else if (parts.has("BAPD")) {
                return "BAPD";
            } else {
                return "APD";
            }
        } else if (coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_AUTO_LIABILITY && parts.has("BAL")) {
            return "BAL";
        }
        return CoverageOption.getMetadata(coverage.coverageOptionId).defaultAbbreviation;
    }

    static duplicate(coverage: Coverage): Coverage {
        const c = plainToClass(Coverage, {
            ...coverage,
            attributes: coverage.attributes || [],
            limits: coverage.limits || [],
            modifications: coverage.modifications || [],
            underlyingCoverages: coverage.underlyingCoverages || [],
            scheduleOfHazards: coverage.scheduleOfHazards || [],
            rates: coverage.rates || {
                owners: [],
                classifications: [],
            },
        });

        c.id = null;
        c.createdDate = null;
        c.policyId = null;
        c.policyCreatedDate = null;
        c.policy = null;
        c.attributes = c.attributes.map((a) => Attribute.duplicate(a));
        c.limits = c.limits.map((l) => CoverageLimit.duplicate(l));
        c.modifications = c.modifications.map((m) => CoverageModification.duplicate(m));
        c.rates = {
            owners: c.rates.owners.map((o) => CoverageRateOwner.duplicate(o)),
            classifications: c.rates.classifications.map((c) => CoverageRateClassification.duplicate(c)),
        };
        c.underlyingCoverages = c.underlyingCoverages.map((u) => CoverageUnderlyingCoverage.duplicate(u));
        c.scheduleOfHazards = c.scheduleOfHazards.map((s) => CoverageScheduleOfHazard.duplicate(s));

        return c;
    }

    static createTemplateFrom(coverage: Coverage): Coverage {
        const c = Coverage.duplicate(coverage);

        c.basis = null;
        c.rate = null;
        c.frequency = null;
        c.premium = null;
        c.compositeRatings = coverage.compositeRatings?.map((r) => CoverageCompositeRating.createTemplateFrom(r));
        c.linkedTractors = [];
        c.linkedDrivers = [];
        c.linkedTrailers = [];
        c.linkedBusinessAutos = [];
        c.linkedTools = [];
        c.scheduleOfHazards = [];
        c.underlyingCoverages = [];
        c.rates = {
            owners: [],
            classifications: coverage.rates.classifications.map((c) => CoverageRateClassification.createTemplateFrom(c)),
        };
        c.limits = coverage.limits?.filter((l) => {
            if (l.coverageLimitOptionId === CoverageLimitOption.Id.PROPERTY) return null;
            return l;
        });

        return c;
    }

    static compare(
        { base, compare }: { base?: Coverage; compare?: Coverage },
        options?: { separateEquipment?: boolean }
    ): Coverage.IComparisonReturn {
        const comparison = new Comparison(Coverage, base, compare) as Coverage.IComparisonReturn;
        comparison.setField("coverageOptionId", compare?.coverageOptionId || base?.coverageOptionId);
        comparison.setField("coverageOption", compare?.coverageOption || base?.coverageOption);

        const separateEquipment = options?.separateEquipment ?? true;

        if (!base) {
            return comparison.setNew({
                obj: compare,
                description: `Add ${compare.coverageOption.name} coverage`,
                subComparison: comparison as Comparison<unknown>,
                label: "Coverage",
            });
        }

        if (!compare) {
            return comparison.setDelete({
                obj: base,
                description: `Delete ${base.coverageOption.name} coverage`,
                subComparison: comparison as Comparison<unknown>,
                label: "Coverage",
            });
        }

        [
            {
                key: "premium",
                label: "Premium",
                transform: (v) => (v ? NumberFormatter.Currency.format(v) : "n/a"),
            },
            {
                key: "basis",
                label: "Basis",
                transform: (v) => (v ? v : "n/a"),
            },
            {
                key: "rate",
                label: "Rate",
                transform: (v) => (v ? NumberFormatter.Percent.format(v) : "n/a"),
            },
            {
                key: "frequency",
                label: "Frequency",
                transform: (v) => (v ? v : "n/a"),
            },
        ].forEach(({ key, label, transform }) => {
            if (base[key] !== compare[key]) {
                comparison.addDiff({
                    type: "change",
                    description: `Update ${label} from ${transform(base[key])} to ${transform(compare[key])}`,
                    label,
                    priority: null,
                    fieldName: key as keyof Coverage,
                    isArrayField: false,
                    value: {
                        from: base[key],
                        to: compare[key],
                        base,
                        compare,
                    },
                });
            }
        });

        Comparison.compareArrayFields<Coverage, "compositeRatings">({
            comparison,
            baseEntity: base,
            compareEntity: compare,
            fieldName: "compositeRatings",
            // Descriptions are not unique, so include the row's occurrence among
            // ratings with the same description in its comparison identity.
            getFieldKey(entity, index, entities) {
                const occurrence = entities.slice(0, index).filter((rating) => rating.description === entity.description).length;
                return `${entity.description}\0${occurrence}`;
            },
            itemDescriptionName: "Composite Rating",
            getItemDescription(entity) {
                return entity.toString();
            },
        });

        if (!separateEquipment) {
            Comparison.compareArrayFields<Coverage, "linkedTractors">({
                comparison,
                baseEntity: base,
                compareEntity: compare,
                fieldName: "linkedTractors",
                fieldKey: "tractorId",
                itemDescriptionName: "Tractor",
                getItemDescription(entity) {
                    return entity.toString();
                },
            });

            Comparison.compareArrayFields<Coverage, "linkedTrailers">({
                comparison,
                baseEntity: base,
                compareEntity: compare,
                fieldName: "linkedTrailers",
                fieldKey: "trailerId",
                itemDescriptionName: "Trailer",
                getItemDescription(entity) {
                    return entity.toString();
                },
            });

            Comparison.compareArrayFields<Coverage, "linkedBusinessAutos">({
                comparison,
                baseEntity: base,
                compareEntity: compare,
                fieldName: "linkedBusinessAutos",
                fieldKey: "businessAutoId",
                itemDescriptionName: "Bus Auto",
                getItemDescription(entity) {
                    return entity.toString();
                },
            });

            Comparison.compareArrayFields<Coverage, "linkedTools">({
                comparison,
                baseEntity: base,
                compareEntity: compare,
                fieldName: "linkedTools",
                fieldKey: "toolId",
                itemDescriptionName: "Tool",
                getItemDescription(entity) {
                    return entity.toString();
                },
            });

            Comparison.compareArrayFields<Coverage, "linkedDrivers">({
                comparison,
                baseEntity: base,
                compareEntity: compare,
                fieldName: "linkedDrivers",
                fieldKey: "driverId",
                itemDescriptionName: "Driver",
                getItemDescription(entity) {
                    return entity.toString();
                },
            });
        }

        Comparison.compareArrayFields<Coverage, "attributes">({
            comparison,
            baseEntity: plainToClass(Coverage, {
                coverageOption: base.coverageOption,
                coverageOptionId: base.coverageOptionId,
                attributes: Attribute.filterOutEquipmentAndDriverAttributes(base.attributes),
            }),
            compareEntity: plainToClass(Coverage, {
                coverageOption: compare.coverageOption,
                coverageOptionId: compare.coverageOptionId,
                attributes: Attribute.filterOutEquipmentAndDriverAttributes(compare.attributes),
            }),
            fieldName: "attributes",
            fieldKey: "attributeOptionId",
            itemDescriptionName: "Attribute",
            getItemDescription(entity) {
                return entity.attributeOption.name;
            },
        });

        Comparison.compareArrayFields<Coverage, "modifications">({
            comparison,
            baseEntity: base,
            compareEntity: compare,
            fieldName: "modifications",
            fieldKey: "modificationOptionId",
            itemDescriptionName: "Modification",
            getItemDescription(entity) {
                return entity.modificationOption.name;
            },
        });

        Comparison.compareArrayFields<Coverage, "scheduleOfHazards">({
            comparison,
            baseEntity: base,
            compareEntity: compare,
            fieldName: "scheduleOfHazards",
            fieldKey: "propertyId",
            itemDescriptionName: "Location",
            getItemDescription(entity) {
                return entity.property?.street1 || entity.toString();
            },
        });

        Comparison.compareArrayFields<Coverage, "underlyingCoverages">({
            comparison,
            baseEntity: base,
            compareEntity: compare,
            fieldName: "underlyingCoverages",
            fieldKey: "description",
            itemDescriptionName: "Underlying Coverage",
            getItemDescription(entity) {
                return entity.description;
            },
        });

        Comparison.compareArrayFields<Coverage, "limits">({
            comparison,
            baseEntity: base,
            compareEntity: compare,
            fieldName: "limits",
            fieldKey: "comparisonKey",
            itemDescriptionName: "Limit",
            getItemDescription(entity) {
                return entity.toString();
            },
        });

        return comparison;
    }

    static new(cvg: Partial<Coverage>): Coverage {
        return plainToClass(Coverage, cvg || {});
    }

    id: number;
    createdDate: Date;

    policyId: string;
    policyCreatedDate: Date;
    policy?: Policy;

    sortOrder: number;
    premium?: number;
    basis: CoverageEnums.Basis;
    rate: number;
    frequency: CoverageEnums.Frequency;
    coverageOptionId: CoverageOption.Id;
    @Type(() => CoverageOption)
    coverageOption?: CoverageOption;

    @Type(() => CoverageCompositeRating)
    compositeRatings?: CoverageCompositeRating[];

    @Type(() => Attribute)
    attributes: Attribute[];

    @Type(() => CoverageLimit)
    limits: CoverageLimit[];

    @Type(() => CoverageLinkedBusinessAuto)
    linkedBusinessAutos?: CoverageLinkedBusinessAuto[];

    @Type(() => CoverageLinkedTractor)
    linkedTractors?: CoverageLinkedTractor[];

    @Type(() => CoverageLinkedTrailer)
    linkedTrailers?: CoverageLinkedTrailer[];

    @Type(() => CoverageLinkedDriver)
    linkedDrivers?: CoverageLinkedDriver[];

    @Type(() => CoverageLinkedTool)
    linkedTools?: CoverageLinkedTool[];

    @Type(() => CoverageModification)
    modifications?: CoverageModification[];
    rates?: {
        owners: CoverageRateOwner[];
        classifications: CoverageRateClassification[];
    };

    @Type(() => CoverageUnderlyingCoverage)
    underlyingCoverages?: CoverageUnderlyingCoverage[];

    @Type(() => CoverageScheduleOfHazard)
    scheduleOfHazards?: CoverageScheduleOfHazard[];
    metadata?: Record<string, unknown>;

    compare(comparisonCoverage: Coverage, options?: { isBase?: boolean; separateEquipment?: boolean }): Comparison<Coverage> {
        return Coverage.compare(
            Comparison.getBaseAndComparisonObjects(
                {
                    initiatorEntity: this,
                    comparisonEntity: comparisonCoverage,
                },
                {
                    isBase: options?.isBase,
                }
            ),
            {
                separateEquipment: options?.separateEquipment,
            }
        );
    }

    toString(): string {
        const nf = NumberFormatter.Currency;
        const parts: string[] = [];

        if (this.coverageOption?.name) parts.push(this.coverageOption?.name);
        if (this.premium) parts.push(nf.format(this.premium));
        if (this.basis) parts.push(this.basis);
        if (this.rate) parts.push(nf.format(this.rate));
        if (this.frequency) parts.push(this.frequency);

        return parts.join(", ");
    }

    /**
     * Returns a copy of the provided coverage with the tractors linked and required attributes added. The tractors should be ensured
     * to not already be on the coverage before calling this method, otherwise duplicate tractors and attributes will be added.
     */
    public withTractors(
        units: Pick<Tractor, "id" | "collision" | "comprehensive" | "value" | "ownerOperator" | "acv" | "spare">[]
    ): Coverage {
        const coverage = cloneDeep(this);
        units.forEach((unit) => {
            coverage.linkedTractors.push({
                tractorId: unit.id,
                coverageId: coverage.id,
                coverageCreatedDate: coverage.createdDate,
            } as CoverageLinkedTractor);
            if (unit.value && !unit.acv && coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE) {
                coverage.attributes.push(
                    Attribute.new.valuationStatedValue({
                        tractorId: unit.id,
                        coverage,
                        value: unit.value,
                    })
                );
            }
            if (unit.acv && coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE) {
                coverage.attributes.push(
                    Attribute.new.valuationActualCashValue({
                        tractorId: unit.id,
                        coverage,
                    })
                );
            }
            if (unit.ownerOperator) {
                coverage.attributes.push(
                    Attribute.new.unitOwnerOperator({
                        tractorId: unit.id,
                        coverage,
                    })
                );
            }
            if (unit.spare && coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_AUTO_LIABILITY) {
                coverage.attributes.push(
                    Attribute.new.unitSpareRated({
                        tractorId: unit.id,
                        coverage,
                    })
                );
            }
            if (coverage.limits.some((l) => l.coverageLimitOptionId === CoverageLimitOption.Id.COLLISION) && unit.collision) {
                coverage.attributes.push(
                    Attribute.new.unitCollision({
                        tractorId: unit.id,
                        coverage,
                    })
                );
            }
            if (coverage.limits.some((l) => l.coverageLimitOptionId === CoverageLimitOption.Id.COMPREHENSIVE) && unit.comprehensive) {
                coverage.attributes.push(
                    Attribute.new.unitComprehensive({
                        tractorId: unit.id,
                        coverage,
                    })
                );
            }
        });
        return coverage;
    }

    /**
     * Returns a copy of the provided coverage with the tractors and their attributes removed.
     */
    public withoutTractors(ids: number[]): Coverage {
        const coverage = cloneDeep(this);
        coverage.linkedTractors = coverage.linkedTractors.filter((lt) => !ids.includes(lt.tractorId));
        coverage.attributes = coverage.attributes.filter((a) => !a.tractorId || !ids.includes(a.tractorId));
        return coverage;
    }

    /**
     * Returns a copy of the provided coverage with the trailers linked and required attributes added. The trailers should be ensured
     * to not already be on the coverage before calling this method, otherwise duplicate trailers and attributes will be added.
     */
    public withTrailers(
        units: Pick<Trailer, "id" | "value" | "acv" | "ownerOperator" | "comprehensive" | "collision" | "spare">[]
    ): Coverage {
        const coverage = cloneDeep(this);
        units.forEach((unit) => {
            coverage.linkedTrailers.push({
                trailerId: unit.id,
                coverageId: coverage.id,
                coverageCreatedDate: coverage.createdDate,
            } as CoverageLinkedTrailer);

            if (unit.value && !unit.acv && coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE) {
                coverage.attributes.push(Attribute.new.valuationStatedValue({ trailerId: unit.id, coverage, value: unit.value }));
            }
            if (unit.acv && coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_PHYSICAL_DAMAGE) {
                coverage.attributes.push(Attribute.new.valuationActualCashValue({ trailerId: unit.id, coverage }));
            }
            if (unit.ownerOperator) {
                coverage.attributes.push(Attribute.new.unitOwnerOperator({ trailerId: unit.id, coverage }));
            }
            if (unit.spare && coverage.coverageOptionId === CoverageOption.Id.TRUCKERS_AUTO_LIABILITY) {
                coverage.attributes.push({
                    metadata: {
                        autoAdded: true,
                        trailerId: unit.id,
                        coverageType: coverage.coverageOptionId,
                        equipmentType: "trailer",
                        valueRequired: false,
                    } as Record<string, unknown>,
                    attributeOptionId: AttributeOption.Id.UNIT_SPARE_RATED,
                    trailerId: unit.id,
                    coverageId: coverage.id,
                    coverageCreatedDate: coverage.createdDate,
                } as Attribute);
            }
            if (coverage.limits.some((l) => l.coverageLimitOptionId === CoverageLimitOption.Id.COLLISION) && unit.collision) {
                coverage.attributes.push({
                    metadata: {
                        autoAdded: true,
                        trailerId: unit.id,
                        coverageType: coverage.coverageOptionId,
                        equipmentType: "trailer",
                        valueRequired: false,
                    } as Record<string, unknown>,
                    attributeOptionId: AttributeOption.Id.UNIT_COLLISION,
                    trailerId: unit.id,
                    coverageId: coverage.id,
                    coverageCreatedDate: coverage.createdDate,
                } as Attribute);
            }
            if (coverage.limits.some((l) => l.coverageLimitOptionId === CoverageLimitOption.Id.COMPREHENSIVE) && unit.comprehensive) {
                coverage.attributes.push({
                    metadata: {
                        autoAdded: true,
                        trailerId: unit.id,
                        coverageType: coverage.coverageOptionId,
                        equipmentType: "trailer",
                        valueRequired: false,
                    } as Record<string, unknown>,
                    attributeOptionId: AttributeOption.Id.UNIT_COMPREHENSIVE,
                    trailerId: unit.id,
                    coverageId: coverage.id,
                    coverageCreatedDate: coverage.createdDate,
                } as Attribute);
            }
        });
        return coverage;
    }

    /**
     * Returns a copy of the provided coverage with the trailers and their attributes removed.
     */
    public withoutTrailers(ids: number[]): Coverage {
        const coverage = cloneDeep(this);
        coverage.linkedTrailers = coverage.linkedTrailers.filter((lt) => !ids.includes(lt.trailerId));
        coverage.attributes = coverage.attributes.filter((a) => !a.trailerId || !ids.includes(a.trailerId));
        return coverage;
    }

    /**
     * Returns a copy of the provided coverage with the business autos linked and required attributes added. The business autos should be ensured
     * to not already be on the coverage before calling this method, otherwise duplicate business autos and attributes will be added.
     */
    public withBusinessAutos(units: Pick<BusinessAuto, "id" | "value" | "acv" | "collision" | "comprehensive">[]): Coverage {
        const coverage = cloneDeep(this);
        units.forEach((unit) => {
            coverage.linkedBusinessAutos.push({
                businessAutoId: unit.id,
                coverageId: coverage.id,
                coverageCreatedDate: coverage.createdDate,
            } as CoverageLinkedBusinessAuto);

            if (unit.value && !unit.acv && coverage.coverageOptionId === CoverageOption.Id.BUSINESS_AUTO_LIABILITY) {
                coverage.attributes.push({
                    metadata: {
                        autoAdded: true,
                        businessAutoId: unit.id,
                        coverageType: coverage.coverageOptionId,
                        equipmentType: "businessAuto",
                        valueRequired: true,
                    } as Record<string, unknown>,
                    attributeOptionId: AttributeOption.Id.STATED_VALUE,
                    valueNumber: unit.value,
                    businessAutoId: unit.id,
                    coverageId: coverage.id,
                    coverageCreatedDate: coverage.createdDate,
                } as Attribute);
            }
            if (unit.acv && coverage.coverageOptionId === CoverageOption.Id.BUSINESS_AUTO_LIABILITY) {
                coverage.attributes.push({
                    metadata: {
                        autoAdded: true,
                        businessAutoId: unit.id,
                        coverageType: coverage.coverageOptionId,
                        equipmentType: "businessAuto",
                        valueRequired: false,
                    } as Record<string, unknown>,
                    attributeOptionId: AttributeOption.Id.VALUATION_ACTUAL_CASH_VALUE,
                    businessAutoId: unit.id,
                    coverageId: coverage.id,
                    coverageCreatedDate: coverage.createdDate,
                } as Attribute);
            }
            if (coverage.limits.some((l) => l.coverageLimitOptionId === CoverageLimitOption.Id.COLLISION) && unit.collision) {
                coverage.attributes.push({
                    metadata: {
                        autoAdded: true,
                        businessAutoId: unit.id,
                        coverageType: coverage.coverageOptionId,
                        equipmentType: "businessAuto",
                        valueRequired: false,
                    } as Record<string, unknown>,
                    attributeOptionId: AttributeOption.Id.UNIT_COLLISION,
                    businessAutoId: unit.id,
                    coverageId: coverage.id,
                    coverageCreatedDate: coverage.createdDate,
                } as Attribute);
            }
            if (coverage.limits.some((l) => l.coverageLimitOptionId === CoverageLimitOption.Id.COMPREHENSIVE) && unit.comprehensive) {
                coverage.attributes.push({
                    metadata: {
                        autoAdded: true,
                        businessAutoId: unit.id,
                        coverageType: coverage.coverageOptionId,
                        equipmentType: "businessAuto",
                        valueRequired: false,
                    } as Record<string, unknown>,
                    attributeOptionId: AttributeOption.Id.UNIT_COMPREHENSIVE,
                    businessAutoId: unit.id,
                    coverageId: coverage.id,
                    coverageCreatedDate: coverage.createdDate,
                } as Attribute);
            }
        });
        return coverage;
    }

    /**
     * Returns a copy of the provided coverage with the business autos and their attributes removed.
     */
    public withoutBusinessAutos(ids: number[]): Coverage {
        const coverage = cloneDeep(this);
        coverage.linkedBusinessAutos = coverage.linkedBusinessAutos.filter((l) => !ids.includes(l.businessAutoId));
        coverage.attributes = coverage.attributes.filter((a) => !a.businessAutoId || !ids.includes(a.businessAutoId));
        return coverage;
    }

    /**
     * Returns a copy of the provided coverage with the tools linked and required attributes added. The tools should be ensured
     * to not already be on the coverage before calling this method, otherwise duplicate tools and attributes will be added.
     */
    public withTools(units: Pick<InlandMarineEquipment, "id" | "value" | "acv">[]): Coverage {
        const coverage = cloneDeep(this);
        units.forEach((unit) => {
            coverage.linkedTools.push({
                toolId: unit.id,
                coverageId: coverage.id,
                coverageCreatedDate: coverage.createdDate,
            } as CoverageLinkedTool);

            if (unit.value && !unit.acv && coverage.coverageOptionId === CoverageOption.Id.INLAND_MARINE) {
                coverage.attributes.push({
                    metadata: {
                        autoAdded: true,
                        toolId: unit.id,
                        coverageType: coverage.coverageOptionId,
                        equipmentType: "tool",
                        valueRequired: true,
                    } as Record<string, unknown>,
                    attributeOptionId: AttributeOption.Id.STATED_VALUE,
                    valueNumber: unit.value,
                    toolId: unit.id,
                    coverageId: coverage.id,
                    coverageCreatedDate: coverage.createdDate,
                } as Attribute);
            }
            if (unit.acv && coverage.coverageOptionId === CoverageOption.Id.INLAND_MARINE) {
                coverage.attributes.push({
                    metadata: {
                        autoAdded: true,
                        toolId: unit.id,
                        coverageType: coverage.coverageOptionId,
                        equipmentType: "tool",
                        valueRequired: false,
                    } as Record<string, unknown>,
                    attributeOptionId: AttributeOption.Id.VALUATION_ACTUAL_CASH_VALUE,
                    toolId: unit.id,
                    coverageId: coverage.id,
                    coverageCreatedDate: coverage.createdDate,
                } as Attribute);
            }
        });
        return coverage;
    }

    /**
     * Returns a copy of the provided coverage with the tools and their attributes removed.
     */
    public withoutTools(ids: number[]): Coverage {
        const coverage = cloneDeep(this);
        coverage.linkedTools = coverage.linkedTools.filter((l) => !ids.includes(l.toolId));
        coverage.attributes = coverage.attributes.filter((a) => !a.toolId || !ids.includes(a.toolId));
        return coverage;
    }

    /**
     * Returns a copy of the provided coverage with the drivers linked and required attributes added. The drivers should be ensured
     * to not already be on the coverage before calling this method, otherwise duplicate drivers and attributes will be added.
     */
    public withDrivers(drivers: Pick<Driver, "id" | "doh" | "exp" | "ownerOperator">[]): Coverage {
        const coverage = cloneDeep(this);
        drivers.forEach((driver) => {
            coverage.linkedDrivers.push({
                driverId: driver.id,
                coverageId: coverage.id,
                coverageCreatedDate: coverage.createdDate,
            } as CoverageLinkedDriver);

            coverage.attributes.push(
                Attribute.new.driverHireDate({
                    coverage,
                    driverId: driver.id,
                    value: driver.doh,
                }),
                Attribute.new.driverYearsExperience({
                    coverage,
                    driverId: driver.id,
                    value: driver.exp,
                })
            );
            if (driver.ownerOperator) {
                coverage.attributes.push(
                    Attribute.new.unitOwnerOperator({
                        coverage,
                        driverId: driver.id,
                    })
                );
            }
        });
        return coverage;
    }

    /**
     * Returns a copy of the provided coverage with the drivers and their attributes removed.
     */
    public withoutDrivers(ids: number[]): Coverage {
        const coverage = cloneDeep(this);
        coverage.linkedDrivers = coverage.linkedDrivers.filter((ld) => !ids.includes(ld.driverId));
        coverage.attributes = coverage.attributes.filter((a) => !a.driverId || !ids.includes(a.driverId));
        return coverage;
    }

    /**
     * Upserts attributes into the coverage, based on equality of attributeOptionId, tractorId, trailerId, businessAutoId, toolId, and driverId.
     * @param attributes
     * @returns
     */
    public withUnitAttributes(attributes: Attribute[]): Coverage {
        const coverage = cloneDeep(this);
        for (const att of attributes) {
            const existing = coverage.attributes.find(
                (a) =>
                    a.attributeOptionId === att.attributeOptionId &&
                    ((a.tractorId && a.tractorId === att.tractorId) ||
                        (a.trailerId && a.trailerId === att.trailerId) ||
                        (a.businessAutoId && a.businessAutoId === att.businessAutoId) ||
                        (a.toolId && a.toolId === att.toolId) ||
                        (a.driverId && a.driverId === att.driverId))
            );
            if (existing) {
                existing.valueNumber = att.valueNumber;
                existing.valueText = att.valueText;
            } else {
                coverage.attributes.push(att);
            }
        }
        return coverage;
    }

    /**
     * Removes attributes from the coverage, based on equality of attributeOptionId, tractorId, trailerId, businessAutoId, toolId, and driverId.
     * @param attributes
     * @returns
     */
    public withoutUnitAttributes(
        attributes: Pick<Attribute, "attributeOptionId" | "tractorId" | "trailerId" | "businessAutoId" | "toolId" | "driverId">[]
    ): Coverage {
        const coverage = cloneDeep(this);
        coverage.attributes = coverage.attributes.filter(
            (a) =>
                !attributes.some((b) => {
                    return (
                        a.attributeOptionId === b.attributeOptionId &&
                        ((a.tractorId && a.tractorId === b.tractorId) ||
                            (a.trailerId && a.trailerId === b.trailerId) ||
                            (a.businessAutoId && a.businessAutoId === b.businessAutoId) ||
                            (a.toolId && a.toolId === b.toolId) ||
                            (a.driverId && a.driverId === b.driverId))
                    );
                })
        );
        return coverage;
    }
}

export declare namespace Coverage {
    export interface IHasCoverageOptionId {
        coverageOptionId: CoverageOption.Id;
    }
    export type Basis = CoverageEnums.Basis;
    export type Frequency = CoverageEnums.Frequency;
    export interface Summary<T extends Coverage.ICanGenerateAbbreviation> extends _Summary<T> {}
    export interface Summaries<T extends Coverage.ICanGenerateAbbreviation> extends _Summaries<T> {}
    export interface IComparisonReturn extends Comparison<Coverage> {
        coverageOptionId: CoverageOption.Id;
        coverageOption?: CoverageOption;
    }

    export interface ICanGenerateAbbreviation extends Coverage.IHasCoverageOptionId {
        linkedBusinessAutos?: unknown[];
        linkedTractors?: unknown[];
        linkedTrailers?: unknown[];
        underlyingCoverages?: Pick<CoverageUnderlyingCoverage, "description">[];
        attributes?: Attribute[];
        limits?: Pick<CoverageLimit, "coverageLimitOptionId">[];
    }
}
